import apiClient from './client';

// ============================================================================
// Types
// ============================================================================

export type AbsenceQueueStatus = 'pending' | 'sent' | 'skipped' | 'no_template';

export type EscalationTier =
  | 'first_warning'
  | 'first_warning_with_offer'
  | 'mid_escalation'
  | 'deferral_offer'
  | 'forced_deferral'
  | 'no_email';

export interface AttendanceFollowUpEntry {
  id: string;
  session_id: string;
  cohort_participant_id: string;
  participant_email: string | null;
  participant_name: string | null;
  cumulative_absence_count: number;
  absence_type: 'unexcused' | 'business' | 'personal';
  is_first_session: boolean;
  template_id: string | null;
  rendered_subject: string | null;
  rendered_body: string | null;
  coach_name: string | null;
  coach_email: string | null;
  cm_edited: boolean;
  template_updated: boolean;
  status: AbsenceQueueStatus;
  sent_at: string | null;
  sent_by_user_id: string | null;
  skip_note: string | null;
  skipped_at: string | null;
  skipped_by_user_id: string | null;
  override_template_id: string | null;
  created_at: string;
  updated_at: string;
  // Extended fields from cohort-level query
  session_number: number;
  session_date: string;
  session_timezone: string;
  group_name: string;
  sent_by_name: string | null;
  skipped_by_name: string | null;
  template_name: string | null;
}

export interface AttendanceFollowUpSession {
  session_id: string;
  session_number: number;
  session_date: string;
  pending_count: number;
  addressed_count: number;
  entries: AttendanceFollowUpEntry[];
}

export interface AttendanceFollowUpsResponse {
  cohort_id: string;
  cohort_name: string;
  total_pending: number;
  sessions: AttendanceFollowUpSession[];
}

// Summary for banners, callouts, notifications (E19-6: MED-502)
export interface AttendanceFollowUpSummary {
  total_pending: number;
  total_addressed: number;
  total_ever_generated: number;
  sessions_with_pending: number;
  sessions: Array<{
    session_id: string;
    session_number: number;
    session_date: string;
    pending_count: number;
  }>;
  participant_pending_counts: Record<string, number>;
  user_pending_counts: Record<string, number>;
}

export interface BatchSendResult {
  sent_count: number;
  failed_count: number;
  skipped_count: number;
  results: Array<{
    entry_id: string;
    participant_name: string | null;
    template_name: string | null;
    coach_email: string | null;
    status: 'sent' | 'failed';
    error?: string;
  }>;
}

// ============================================================================
// MED-738 Level 1 / Level 2 types
// ============================================================================

export interface FollowUpParticipantSummary {
  cohort_participant_id: string;
  user_id: string;
  participant_name: string;
  participant_email: string | null;
  total_email_count: number;
  pending_count: number;
  past_sent_count: number;
  past_skipped_count: number;
  primary_session_id: string;
  primary_session_number: number;
  primary_session_date: string;
  primary_session_timezone: string;
  primary_status: AbsenceQueueStatus;
  primary_absence_count: number;
  primary_absence_type: 'unexcused' | 'business' | 'personal';
  primary_template_tier: EscalationTier | null;
  primary_template_name: string | null;
  group_id: string | null;
  group_name: string | null;
}

export interface FollowUpParticipantsResponse {
  cohort_id: string;
  cohort_name: string;
  total_participants: number;
  total_pending: number;
  total_past: number;
  participants: FollowUpParticipantSummary[];
}

export interface FollowUpParticipantDetailResponse {
  participant_name: string;
  group_name: string;
  coach_name: string | null;
  coach_email: string | null;
  pending_count: number;
  past_count: number;
  entries: AttendanceFollowUpEntry[];
}

export interface AbsenceEmailTemplate {
  id: string;
  name: string;
  subject_template: string;
  body_template: string;
  escalation_tier: EscalationTier;
  is_active: boolean;
  auto_selectable: boolean;
}

// ============================================================================
// API Methods
// ============================================================================

export const attendanceFollowUpsApi = {
  /**
   * Get lightweight follow-up summary (counts, per-session, per-participant).
   */
  summary: async (cohortId: string): Promise<AttendanceFollowUpSummary> => {
    return apiClient.get<AttendanceFollowUpSummary>(
      `/api/cohorts/${cohortId}/attendance-follow-ups/summary`,
    );
  },

  /**
   * Get all attendance follow-up queue entries for a cohort, grouped by session.
   */
  list: async (
    cohortId: string,
    filters?: { session_id?: string; status?: string },
  ): Promise<AttendanceFollowUpsResponse> => {
    const params = new URLSearchParams();
    if (filters?.session_id) params.set('session_id', filters.session_id);
    if (filters?.status) params.set('status', filters.status);
    const qs = params.toString();
    return apiClient.get<AttendanceFollowUpsResponse>(
      `/api/cohorts/${cohortId}/attendance-follow-ups${qs ? `?${qs}` : ''}`,
    );
  },

  /**
   * MED-738 Level 1: One row per participant with aggregate pending/past counts.
   */
  listParticipants: async (
    cohortId: string,
    filters?: { session_id?: string; status?: string },
  ): Promise<FollowUpParticipantsResponse> => {
    const params = new URLSearchParams();
    if (filters?.session_id) params.set('session_id', filters.session_id);
    if (filters?.status) params.set('status', filters.status);
    const qs = params.toString();
    return apiClient.get<FollowUpParticipantsResponse>(
      `/api/cohorts/${cohortId}/attendance-follow-ups/participants${qs ? `?${qs}` : ''}`,
    );
  },

  /**
   * MED-738 Level 2: All follow-ups for a single participant in this cohort.
   * participantId is the cohort_participant_id.
   */
  getParticipantFollowUps: async (
    cohortId: string,
    participantId: string,
  ): Promise<FollowUpParticipantDetailResponse> => {
    return apiClient.get<FollowUpParticipantDetailResponse>(
      `/api/cohorts/${cohortId}/participants/${participantId}/absence-follow-ups`,
    );
  },

  /**
   * List all absence email templates for the override dropdown (MED-738).
   * Returns active + inactive; caller filters as needed.
   */
  listTemplates: async (): Promise<AbsenceEmailTemplate[]> => {
    return apiClient.get<AbsenceEmailTemplate[]>('/api/absence-email-templates');
  },

  /**
   * Update a queue entry's subject, body, send/skip status, skip note, or
   * override template (MED-738 extends MED-499).
   */
  updateEntry: async (
    cohortId: string,
    entryId: string,
    data: {
      rendered_subject?: string;
      rendered_body?: string;
      status?: 'pending' | 'skipped';
      skip_note?: string | null;
      override_template_id?: string | null;
    },
  ): Promise<AttendanceFollowUpEntry> => {
    return apiClient.patch<AttendanceFollowUpEntry>(
      `/api/cohorts/${cohortId}/attendance-follow-ups/${entryId}`,
      data,
    );
  },

  /**
   * Batch send all pending attendance follow-up emails.
   */
  sendBatch: async (
    cohortId: string,
    options?: { sessionId?: string; entryIds?: string[] },
  ): Promise<BatchSendResult> => {
    const payload: Record<string, unknown> = {};
    if (options?.sessionId) payload.session_id = options.sessionId;
    if (options?.entryIds && options.entryIds.length > 0) payload.entry_ids = options.entryIds;
    return apiClient.post<BatchSendResult>(
      `/api/cohorts/${cohortId}/attendance-follow-ups/send`,
      payload,
    );
  },

  /**
   * Override duplicate prevention — reset sent entry to pending.
   */
  overrideEntry: async (cohortId: string, entryId: string): Promise<AttendanceFollowUpEntry> => {
    return apiClient.post<AttendanceFollowUpEntry>(
      `/api/cohorts/${cohortId}/attendance-follow-ups/${entryId}/override`,
      {},
    );
  },
};

export default attendanceFollowUpsApi;
